import { useRouter } from 'next/router'
import Script from 'next/script'
import { parseCookies, setCookie } from 'nookies'
import React, { useEffect } from 'react'
import { useDelayedLoad } from 'src/hooks/useDelayedLoad'
import { useIdleLoad } from 'src/hooks/useIdleLoad'
import {
  isRedactedTokenRoute,
  shouldSuppressCapabilityTelemetry,
  touchesRedactedToken
} from 'src/utils/capabilityPrivacy'
import ga from 'src/utils/pixels/gtag'
import { getHsq } from 'src/utils/pixels/hubspot'
import posthog from 'src/utils/pixels/posthog'
import { readStoredConsent } from 'src/utils/storedCookieConsent'
import config from '../../src/config'
import {
  ConsoleMessage,
  FacebookPixelSrc,
  gtagScript,
  LinkedInScript1,
  LinkedInScript2,
  microsoftScript,
  OpenAiScript
} from '../../src/constants/scripts'
import fbp from '../utils/pixels/fbp'
import oaiq from '../utils/pixels/oaiq'

// Disable HubSpot chat widget while keeping the tracking pixel active.
// Must run before the hs-scripts loader fires.
const HubSpotChatDisableScript = `window.hsConversationsSettings = Object.assign(window.hsConversationsSettings || {}, { loadImmediately: false });`

// Upper bound on how long gtag/js + the HubSpot loader wait after hydration
// when the main thread never goes idle (cheap Android). See `librariesIdle`.
const LIBRARY_IDLE_TIMEOUT_MS = 3000

// JS-disabled crawler fallback for Meta + LinkedIn. Inert when JS runs (the
// browser never fetches <noscript> contents). Shared so the token-in-query
// branch in `Scripts` can keep the SERVER's markup — see the comment there.
const noscriptFallback = (
  <noscript
    suppressHydrationWarning
    dangerouslySetInnerHTML={{
      __html: `<img class="hidden size-px" alt="" src="https://www.facebook.com/tr?id=${config.pixels.fb}&ev=PageView&noscript=1"/><img class="hidden size-px" alt="" src="https://px.ads.linkedin.com/collect/?pid=7246484&fmt=gif"/>`
    }}
  />
)

function Scripts() {
  const router = useRouter()
  // Defer non-critical pixels (LinkedIn, Bing UET, HubSpot) until the
  // user shows intent — scroll, click, tap, or 20s elapsed. Bouncers never
  // download them.
  const shouldLoadDelayedPixels = useDelayedLoad()
  // Facebook still rides the 2s fuse — Meta attribution matters for paid
  // acquisition but the ~800 kB FB pixel doesn't earn its keep on
  // bouncers. PostHog used to share this fuse but moved to
  // requestIdleCallback-eager init below — see that useEffect for why.
  const shouldLoadFbPixel = useDelayedLoad(2000)
  // The two heavy attribution LIBRARIES — Google tag (gtag/js) and the
  // HubSpot loader — wait for the browser to go idle after hydration, or 3 s,
  // whichever comes first (Jules, 2026-09-25). NOT an intent fuse: a tap that
  // triggered the download would land the ~600 ms (4× CPU) of gtag evaluation
  // inside that tap's own INP window. Their inline stubs (`gtag`/`dataLayer`,
  // `_hsq`) still go in immediately, so every call made meanwhile is queued
  // and replays in order once the library arrives. Ads conversions are not
  // affected: over 30 d (MX) zero primary conversions fired within 3 s of
  // session start and one within 5 s; only directional events sit in that
  // window (`contact_wa`: 2 of 212 in the 3–5 s band) and they were already
  // lost before the library loaded — this widens that window by ~1–3 s.
  // Re-run the timing query in docs/performance/interaction-latency.md before
  // widening the timeout.
  const librariesIdle = useIdleLoad(LIBRARY_IDLE_TIMEOUT_MS)

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      ConsoleMessage,
      'color: #009DF4; font-size: 20px; font-weight: bold; text-shadow: 1px 1px #000;',
      'color: #EE5568; font-weight: bold;',
      'color: #00B37C; font-size: 14px; font-weight: bold;',
      'color: #00C48C; font-style: italic; font-weight: bold;'
    )
  }, [])

  // PostHog — initialize as soon as the browser is idle after hydration.
  //
  // Previously gated on the 2s intent fuse (`shouldLoadFbPixel`), which
  // missed first-click conversions: the same click that tripped the fuse
  // also fired `add_to_cart` / `contact_wa` etc., and the dynamic import
  // hadn't resolved yet so `window.posthog` was undefined → silent drop.
  // Confirmed via PostHog MCP query: 78 sessions, 0 SpotMe custom events
  // over a 2.3 h window despite multiple listing views.
  //
  // The queue inside `posthog.ts` handles the brief sub-100ms window
  // between this useEffect firing and the dynamic import resolving —
  // any `posthog.event(...)` call in that window is buffered and
  // drained in order. `requestIdleCallback` keeps the ~50 kB import
  // off the LCP / hydration path; setTimeout(0) is the Safari fallback.
  useEffect(() => {
    if (!config.isProd || shouldSuppressCapabilityTelemetry()) return

    const fire = () => void posthog.init()

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(fire, { timeout: 1500 })
      return () => window.cancelIdleCallback(handle)
    }

    const handle = window.setTimeout(fire, 0)
    return () => window.clearTimeout(handle)
  }, [])

  // Meta's `fbq` queue (+ base init and the stored consent) goes in before
  // anything can call it; only the library download waits for the 2s fuse.
  // Same pages as the pixel itself: never on a capability or token document.
  useEffect(() => {
    if (shouldSuppressCapabilityTelemetry()) return
    if (!touchesRedactedToken(window.location.href)) fbp.install()
  }, [])

  useEffect(() => {
    if (shouldSuppressCapabilityTelemetry()) return
    // Redacted token documents (capabilityPrivacy.ts — the token page, or
    // login/KYC carrying it in the query): no ad pixels at all — they read the
    // URL directly and cannot be redacted. PostHog stays.
    if (!touchesRedactedToken(window.location.href)) {
      fbp.pageview()
      // OpenAI/ChatGPT Ads `page_viewed` — the `contents` shape with no
      // amount. Mirrors fbp.pageview() so SPA route changes count too.
      oaiq.contents('page_viewed')
    }

    const handleRouteChange = (url: URL) => {
      if (shouldSuppressCapabilityTelemetry(url.toString())) return
      // ga.pageview() mirrors to PostHog automatically (see gtag.ts).
      // Every GA4/Vercel Analytics event goes to PostHog via the same
      // single mirror point — no caller needs to fire twice. On a token
      // page gtag is never loaded, so only the (redacted) PostHog leg runs.
      ga.pageview(url)
      if (touchesRedactedToken(url.toString())) return
      fbp.pageview()
      oaiq.contents('page_viewed')
      getHsq().push(['trackPageView'])
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    const cookies = parseCookies()
    const sessionId = cookies.session_id
    const maxAge = 60 * 60 * 24 * 30
    if (!sessionId) {
      setCookie(null, 'session_id', Date.now().toString(), {
        maxAge,
        path: '/'
      })
    } else {
      setCookie(null, 'session_id', sessionId, { maxAge, path: '/' })
    }
    const visitorId = cookies.visitor_id
    if (!visitorId) {
      setCookie(null, 'visitor_id', crypto.randomUUID(), {
        maxAge: 365 * 24 * 60 * 60,
        path: '/'
      })
    }
  }, [])

  // `router.pathname` is the page TEMPLATE (`/equipo/invitacion/[token]`), so
  // SSR and the client agree — no hydration branch on window state.
  if (
    !config.isProd ||
    shouldSuppressCapabilityTelemetry() ||
    isRedactedTokenRoute(router.pathname)
  )
    return null

  // A token can also ride in the QUERY of an ordinary page (`/authenticate?
  // redirectA=…`, the static `/userConfirmation/*` steps' `redirectU`), which
  // SSR cannot see on a static page. Safe to decide from `window` here ONLY
  // because every `<Script>` below (afterInteractive / lazyOnload, Pages
  // Router) renders `null` on BOTH server and client and injects from an
  // effect — so the client keeps just the inert `<noscript>`, which is
  // exactly the server's markup. Never add a DOM-rendering element that this
  // branch drops.
  if (
    typeof window !== 'undefined' &&
    touchesRedactedToken(window.location.href)
  )
    return noscriptFallback

  // The saved cookie-banner choice, read the same way as the branch above
  // (these props only shape `<Script>`s, which render nothing). It sets the
  // Google and Bing consent defaults, and ad pixels with no consent mode of
  // their own (OpenAI, LinkedIn) never load once advertising is turned off;
  // Meta gets `fbq('consent', 'revoke')` from fbp.ts.
  const storedConsent =
    typeof window === 'undefined' ? null : readStoredConsent()
  const adPixelsAllowed = storedConsent?.advertisement !== false

  return (
    <>
      {/* Stub + consent defaults + config go in right away (inline, tiny);
          the gtag/js LIBRARY itself waits for `librariesIdle` — its
          evaluation was the largest single main-thread block on a mobile
          landing (614 ms at 4× CPU, 2026-09-25 trace). Calls made before it
          loads queue in `dataLayer` and replay in order. */}
      <Script
        id="gtag-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gtagScript(storedConsent) }}
      />
      {librariesIdle && (
        <Script
          id="gtag"
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${config.pixels.ga}`}
        />
      )}

      {/* OpenAI / ChatGPT Ads pixel — afterInteractive, deliberately NOT on
          the delayed-intent fuse. It is the attribution anchor for an active
          paid test, and the HubSpot comment below records what lazy-loading
          an attribution pixel costs: paid-click bouncers and fast converters
          land in "(direct)" because the tag never ran before they left.
          The loader itself is a tiny async shim; the SDK it pulls stays off
          the critical path. Wrapper + event mapping: utils/pixels/oaiq.ts. */}
      {adPixelsAllowed && (
        <Script
          id="openai-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: OpenAiScript }}
        />
      )}

      {/*
        React 19 hydration trap: when <noscript> has JSX element children,
        the SSR HTML serializes them as real-looking tags, but the browser
        (with JS enabled) parses the inner content as a SINGLE text node.
        React's virtual tree then has element children while the real DOM
        has one text child → hard #418 on every page load that includes
        Scripts. dangerouslySetInnerHTML makes the inner an opaque string
        on both sides so hydration doesn't attempt to walk it. See React
        issue #16341. JS-disabled crawlers still see and load the pixel
        images because the browser inserts them when JS isn't running.

        suppressHydrationWarning is also load-bearing: FB iOS in-app
        browser (FBAN/FBAV) strips the Facebook pixel <img> from inside
        <noscript> before React hydrates — likely a tracker-blocking
        heuristic. SSR ships `<noscript><img fb/><img linkedin/></noscript>`,
        the DOM React sees at hydration time is `<noscript><img linkedin/></noscript>`,
        and even though dangerouslySetInnerHTML usually short-circuits
        the inner walk, React 19's outer comparison still fires #418 on
        the text-content delta. Suppressing the warning here is safe —
        the inner is server-generated, never user-controlled, and the
        noscript is purely a fallback for JS-disabled crawlers.
      */}
      {noscriptFallback}
      <Script
        id="ahrefs-analytics"
        src="https://analytics.ahrefs.com/analytics.js"
        data-key="quFiEn0dQIf7JJo5n9SDiA"
        strategy="lazyOnload"
      />
      {/* HubSpot loads on the same idle-or-3-s gate as gtag/js — NOT on user
          intent: the earlier lazy-on-intent version created `hubspotutk`
          only after a scroll/tap, so paid-click bouncers and fast converters
          showed as "direct" in HS. Idle-or-3-s still sets the cookie on the
          first pageview for anyone who stays past ~3 s (no form is filled
          faster than that), while the 182 ms (4× CPU) of loader + analytics
          + banner + ads-pixel evaluation leaves the first-tap window. Chat
          widget stays disabled via hsConversationsSettings; forms/CTAs
          unused. The disable snippet MUST render before the loader so it
          runs first — it is inline and immediate, the loader is gated. */}
      <Script
        id="hs-chat-disable"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: HubSpotChatDisableScript }}
      />
      {librariesIdle && (
        <Script
          id="hs-script-loader"
          strategy="afterInteractive"
          src="//js.hs-scripts.com/47956675.js"
        />
      )}
      {/* FB pixel library on a 2s fuse — fast enough to tag most real
          visitors for Meta attribution, but still off the critical path.
          Calls made before it loads (landing PageView, consent, early
          events) wait in the `fbq` queue installed above, and it replays
          them in order. PostHog used to share this fuse but now inits on
          requestIdleCallback (see useEffect above) — it needs to be ready
          earlier so first-click add_to_cart events aren't dropped. */}
      {shouldLoadFbPixel && (
        <Script id="fb-pixel" strategy="lazyOnload" src={FacebookPixelSrc} />
      )}
      {/* LinkedIn + Bing UET (Microsoft) gated behind user intent or 20s
          idle — bouncers never download them. GA4 (gtag) stays on
          afterInteractive as the primary ad-attribution source.
          Microsoft Clarity was removed 2026-05-13 in favor of PostHog
          (see src/utils/pixels/posthog.ts). */}
      {shouldLoadDelayedPixels && (
        <>
          {adPixelsAllowed && (
            <>
              <Script
                id="linkedin-insight-1"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{ __html: LinkedInScript1 }}
              />
              <Script
                id="linkedin-insight-2"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{ __html: LinkedInScript2 }}
              />
            </>
          )}
          <Script
            id="microsoft-script"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{ __html: microsoftScript(storedConsent) }}
          />
        </>
      )}
    </>
  )
}

export default React.memo(Scripts)
